/**
 * AIShield Telemetry Response Adapters
 * Maps backend snake_case models to frontend domain types.
 * Preserves null scores, latencies, percentages, subsecond timestamps, and category separations.
 */

import type {
  BackendBundleResponse,
  AdaptedDashboardBundle,
  MetricSummary,
  SidebarCounts,
  ChartBucket,
  RequestRecord,
} from "../types/telemetry.ts";
import { validateDashboardBundle } from "./apiClient.ts";
import { formatBucketLabelIST } from "../utils/dateTime.ts";

/**
 * Adapts raw BackendBundleResponse to AdaptedDashboardBundle for frontend views.
 * Strictly adheres to backend values without recomputing full-window totals or re-multiplying percentages.
 * Validates the raw payload so neither client calls nor direct adapter callers can bypass validation.
 */
export function adaptDashboardBundle(
  raw: BackendBundleResponse
): AdaptedDashboardBundle {
  const validated = validateDashboardBundle(raw);
  const summary: MetricSummary = {
    totalRequests: validated.summary.total_requests,
    // block_rate already represents a percentage from the backend (e.g. 6.6)
    // Preserves null for empty windows; never re-multiplies by 100.
    blockRate:
      validated.summary.block_rate !== null && validated.summary.block_rate !== undefined
        ? Number(validated.summary.block_rate)
        : null,
    allowCount: validated.summary.allow_count,
    flagCount: validated.summary.flag_count,
    blockCount: validated.summary.block_count,
    notEvaluatedCount: validated.summary.not_evaluated_count,
    avgTransitMs:
      validated.summary.avg_transit_ms !== null &&
      validated.summary.avg_transit_ms !== undefined
        ? Number(validated.summary.avg_transit_ms)
        : null,
    avgUpstreamHeaderMs:
      validated.summary.avg_upstream_header_ms !== null &&
      validated.summary.avg_upstream_header_ms !== undefined
        ? Number(validated.summary.avg_upstream_header_ms)
        : null,
    timeoutsCount: validated.summary.timeouts_count,
    failedCount: validated.summary.failed_count,
    aiEvaluatedCount: validated.summary.ai_evaluated_count ?? 0,
    aiNotEvaluatedCount:
      validated.summary.ai_not_evaluated_count ?? validated.summary.total_requests,
    aiSkippedCount: validated.summary.ai_skipped_count ?? 0,
    aiUnreliableRedactionCount: validated.summary.ai_unreliable_redaction_count ?? 0,
    aiFailureCount: validated.summary.ai_failure_count ?? 0,
    aiOtherStatusCount: validated.summary.ai_other_status_count ?? 0,
    aiCoveragePercent:
      validated.summary.ai_coverage_percent !== null &&
      validated.summary.ai_coverage_percent !== undefined
        ? Number(validated.summary.ai_coverage_percent)
        : null,
    avgAiRoundtripMs:
      validated.summary.avg_ai_roundtrip_ms !== null &&
      validated.summary.avg_ai_roundtrip_ms !== undefined
        ? Number(validated.summary.avg_ai_roundtrip_ms)
        : null,
    decisionAttribution: {
      crs: validated.summary.decision_attribution?.crs ?? 0,
      ai: validated.summary.decision_attribution?.ai ?? 0,
      both: validated.summary.decision_attribution?.both ?? 0,
      unknown: validated.summary.decision_attribution?.unknown ?? 0,
    },
    recordedModes: {
      shadow: validated.summary.recorded_modes?.shadow ?? 0,
      hybrid: validated.summary.recorded_modes?.hybrid ?? 0,
      other: validated.summary.recorded_modes?.other ?? 0,
    },
  };

  const sidebarCounts: SidebarCounts = {
    all: validated.sidebar_counts.all,
    blocked: validated.sidebar_counts.blocked,
    flagged: validated.sidebar_counts.flagged,
    failures: validated.sidebar_counts.failures,
  };

  const buckets: ChartBucket[] = validated.chart.buckets.map((b) => ({
    bucketKey: b.bucket_key,
    label: formatBucketLabelIST(b.start_time, validated.window.time_range),
    startTime: b.start_time,
    endTime: b.end_time,
    allow: b.allow,
    flag: b.flag,
    block: b.block,
    notEvaluated: b.not_evaluated,
    total: b.total,
  }));

  const items: RequestRecord[] = validated.table.items.map(adaptRequestRecord);
  const recentSecurityEvents: RequestRecord[] = (
    validated.recent_security_events ?? []
  ).map(adaptRequestRecord);

  return {
    serverTime: validated.server_time,
    window: {
      timeRange: validated.window.time_range,
      timeFrom: validated.window.time_from,
      timeTo: validated.window.time_to,
    },
    summary,
    sidebarCounts,
    chart: {
      bucketSeconds: validated.chart.bucket_seconds,
      buckets,
    },
    table: {
      items,
      total: validated.table.total,
      page: validated.table.page,
      pageSize: validated.table.page_size,
      totalPages: validated.table.total_pages,
    },
    recentSecurityEvents,
    decisionAttribution: summary.decisionAttribution,
    recordedModes: summary.recordedModes,
    aiSummary: {
      aiEvaluatedCount: summary.aiEvaluatedCount,
      aiNotEvaluatedCount: summary.aiNotEvaluatedCount,
      aiSkippedCount: summary.aiSkippedCount,
      aiUnreliableRedactionCount: summary.aiUnreliableRedactionCount,
      aiFailureCount: summary.aiFailureCount,
      aiOtherStatusCount: summary.aiOtherStatusCount,
      aiCoveragePercent: summary.aiCoveragePercent,
      avgAiRoundtripMs: summary.avgAiRoundtripMs,
    },
  };
}

export function adaptRequestRecord(item: RequestRecord): RequestRecord {
  return {
    id: item.id,
    client_request_id: item.client_request_id ?? null,
    timestamp: item.timestamp,
    method: item.method,
    raw_path: item.raw_path,
    query_string: item.query_string ?? null,
    client_ip: item.client_ip,
    headers: item.headers,
    body_bytes: item.body_bytes,
    response_status: item.response_status,
    upstream_status: item.upstream_status,
    gateway_outcome: item.gateway_outcome,
    inspection_status: item.inspection_status,
    blocking_score: item.blocking_score,
    detection_score: item.detection_score,
    inspection_latency_ms: item.inspection_latency_ms,
    policy_mode: item.policy_mode,
    policy_decision: item.policy_decision,
    latency_ms: item.latency_ms,
    upstream_latency_ms: item.upstream_latency_ms,
    error_detail: item.error_detail,
    ai_status: item.ai_status ?? null,
    ai_predicted_category: item.ai_predicted_category ?? null,
    ai_model_probability: item.ai_model_probability ?? null,
    ai_class_probabilities: item.ai_class_probabilities ?? null,
    ai_preprocessor_truncated: item.ai_preprocessor_truncated ?? null,
    ai_token_truncated: item.ai_token_truncated ?? null,
    ai_latency_ms: item.ai_latency_ms ?? null,
    ai_model_identity: item.ai_model_identity ?? null,
    ai_encoder_revision: item.ai_encoder_revision ?? null,
    ai_head_checksum: item.ai_head_checksum ?? null,
    ai_policy_mode: item.ai_policy_mode ?? null,
    crs_policy_decision: item.crs_policy_decision ?? null,
    decision_source: item.decision_source ?? null,
    decision_reason: item.decision_reason ?? null,
    ai_flag_threshold: item.ai_flag_threshold ?? null,
    ai_block_threshold: item.ai_block_threshold ?? null,
  };
}

